import { Box, Typography, Stack, StackProps } from '@mui/material';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';

/* -------------------- Types -------------------- */
type SeriesConfig = {
  key: string;
  name: string;
  color?: string;
};

type AreaLineChartProps = {
  title: string;
  subTitle?: string;
  data: Record<string, any>[] | null;
  xKey: string;
  isCost?: boolean;
  hasTooltip?: boolean;
  tooltipLabel?: TooltipLabel;
  seriesConfig: SeriesConfig[];
  xAxisLabel?: string;
  isLoading?: boolean;
  minTitleHeight?: `${number}px`;
  height?: string;
  hasFilter?: boolean;
  children?: React.ReactNode;
  childrenContainerProps?: StackProps;
  containerProps?: StackProps;
};

/* -------------------- Helpers -------------------- */
const normalizeChartData = (
  data: Record<string, any>[],
  xKey: string,
  seriesConfig: SeriesConfig[],
) => {
  const xAxis = data.map((item) => item[xKey]);
  const series = seriesConfig.map((cfg) => ({
    name: cfg.name,
    color: cfg.color,
    data: data.map((item) => Number(item[cfg.key]) || 0),
  }));
  return { xAxis, series };
};

const formatYAxisValue = (value: number, isCost: boolean) => {
  if (isCost) {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value}`;
  }
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return `${value}`;
};

/* -------------------- Component -------------------- */
const AreaChart = ({
  title,
  subTitle,
  data,
  xKey,
  isCost = false,
  hasTooltip,
  tooltipLabel,
  seriesConfig,
  xAxisLabel,
  isLoading = false,
  minTitleHeight = '38px',
  height,
  hasFilter,
  children,
  childrenContainerProps,
  containerProps,
}: AreaLineChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartRef = useRef<ReactECharts>(null);

  /* Resize */
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    });

    const inst = chartRef.current?.getEchartsInstance();
    if (inst) resizeObserver.observe(inst.getDom());

    return () => resizeObserver.disconnect();
  }, []);

  const chartData = useMemo(() => {
    if (!data || !data.length) return null;
    return normalizeChartData(data, xKey, seriesConfig);
  }, [data, xKey, seriesConfig]);

  const option = useMemo(() => {
    if (!chartData) {
      return { backgroundColor: colors?.widget?.background?.chart };
    }

    return {
      backgroundColor: colors?.widget?.background?.chart,
      textStyle: { color: colors?.widget?.chart?.labelColorSecondary },

      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: any[]) => {
          const date = params?.[0]?.axisValueLabel ?? '';
          let tooltipContent = `<div> ${date}</div>`;

          params.forEach((item) => {
            const val = Number(item?.value) || 0;
            const formatted = formatYAxisValue(val, isCost);
            tooltipContent += `<div><strong>${item.seriesName}</strong>: <strong>${formatted}</strong></div>`;
          });

          return tooltipContent;
        },
      },

      legend: {
        orient: 'vertical',
        right: 48,
        top: 30,
        itemGap: 12,
        icon: 'rect',
        textStyle: { color: colors?.widget?.chart?.legendTextColor },
      },

      grid: {
        left: '5%',
        right: '28%',
        bottom: '10%',
        containLabel: true,
      },

      xAxis: {
        type: 'category',
        data: chartData.xAxis,
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { color: colors?.widget?.chart?.labelColorSecondary },
        axisLabel: { color: colors?.widget?.chart?.labelColorSecondary },
        boundaryGap: false,
      },

      yAxis: {
        type: 'value',
        axisLabel: {
          color: colors?.widget?.chart?.labelColorSecondary,
          formatter: (value: number) => formatYAxisValue(value, isCost),
        },
        splitLine: false,
      },

      series: chartData.series.map((s) => ({
        name: s.name,
        type: 'line',
        data: s.data,
        stack: 'total',
        showSymbol: false,
        itemStyle: { color: s.color },
        lineStyle: { width: 2, color: s.color },
        areaStyle: { opacity: 1, color: s.color },
      })),
    };
  }, [chartData, colors, isCost]);

  return (
    <WidgetWraper>
      <Box sx={{ backgroundColor: colors.widget.background.card }} {...containerProps}>
        {/* Header */}
        <Stack direction="column" gap={2} sx={{ minHeight: minTitleHeight }}>
          <Stack direction="row" alignItems="center">
            <Typography variant="widgetTitleNew" sx={{ color: colors.widget.text.title }}>
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>

          {subTitle && (
            <Typography variant="bodyText" sx={{ color: colors.widget.text.title }}>
              {subTitle}
            </Typography>
          )}
        </Stack>

        {hasFilter && (
          <Stack direction="row" justifyContent="flex-end" mt={1} {...childrenContainerProps}>
            {children}
          </Stack>
        )}

        {/* Chart */}
        <Box height={height ?? 350} mt={2}>
          {!isLoading ? (
            <ReactECharts ref={chartRef} option={option} style={{ height: '100%', width: '100%' }} />
          ) : (
            <LoaderComponent pt={10} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default AreaChart;
